import { useEffect } from 'react';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { getExistingSocket } from '@/lib/socket';
import { sessionSocketListeners } from '@/lib/session-socket-listeners';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSameActionMutation } from '@/hooks/useSingleFlightMutation';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  getCampaigns,
  getCampaign,
  getCampaignStats,
  getCampaignAudience,
  getSegmentationCatalog,
  previewCampaignAudience,
  previewCampaignMessage,
  retryCampaignMessage,
  reconcileCampaignMessage,
  updateCampaignDraft,
  createCampaign,
  startCampaign,
  cancelCampaign,
  pauseCampaign,
  resumeCampaign,
  previewMessage,
  CreateTemplateData,
  UpdateTemplateData,
  CreateCampaignData,
  UpdateCampaignDraftData,
  CampaignAudienceParams,
  PreviewCampaignAudienceData,
  PreviewCampaignMessageData,
  listActiveCampaigns,
} from '@/services/campaigns';
import { getServices } from '@/services/services';
import { getProfessionals } from '@/services/professionals';
import { getLocations } from '@/services/locations';
import { getKanbanColumns } from '@/services/leads';

const TEMPLATES_KEY = 'campaign-templates';
export const CAMPAIGNS_KEY = 'campaigns';
const CAMPAIGN_SEGMENTATION_CATALOG_KEY = 'campaign-segmentation-catalog';
const SEGMENTATION_PAGE_LIMIT = 100;

function appendUniqueById<T extends { id: string }>(items: T[], pageItems: T[]) {
  const knownIds = new Set(items.map((item) => item.id));
  for (const item of pageItems) {
    if (!knownIds.has(item.id)) {
      items.push(item);
      knownIds.add(item.id);
    }
  }
}

async function getAllSegmentationServices() {
  const items: Awaited<ReturnType<typeof getServices>>['data'] = [];
  for (let page = 1; ; page += 1) {
    const result = await getServices({ page, limit: SEGMENTATION_PAGE_LIMIT, isActive: true });
    appendUniqueById(items, result.data);
    if (page >= result.totalPages || result.data.length === 0) break;
  }
  return items;
}

async function getAllSegmentationProfessionals() {
  const items: Awaited<ReturnType<typeof getProfessionals>>['data'] = [];
  for (let page = 1; ; page += 1) {
    const result = await getProfessionals({ page, limit: SEGMENTATION_PAGE_LIMIT });
    appendUniqueById(items, result.data);
    if (page >= result.totalPages || result.data.length === 0) break;
  }
  return items;
}

async function getAllSegmentationLocations() {
  const items: Awaited<ReturnType<typeof getLocations>>['locations'] = [];
  for (let offset = 0; ; offset += SEGMENTATION_PAGE_LIMIT) {
    const result = await getLocations({ active: true, limit: SEGMENTATION_PAGE_LIMIT, offset });
    appendUniqueById(items, result.locations);
    if (offset + result.locations.length >= result.total || result.locations.length === 0) break;
  }
  return items;
}

// =============================================================================
// TEMPLATES HOOKS
// =============================================================================

export function useTemplates() {
  return useQuery({
    queryKey: [TEMPLATES_KEY],
    queryFn: getTemplates,
  });
}

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: [TEMPLATES_KEY, id],
    queryFn: () => getTemplate(id!),
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (data: CreateTemplateData) => createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      toast.success('Modelo criado com sucesso!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao criar modelo';
      toast.error(message);
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTemplateData }) =>
      updateTemplate(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, id] });
      toast.success('Modelo atualizado com sucesso!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao atualizar modelo';
      toast.error(message);
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      toast.success('Modelo removido com sucesso!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao remover modelo';
      toast.error(message);
    },
  });
}

// =============================================================================
// CAMPAIGNS HOOKS
// =============================================================================

export const ACTIVE_CAMPAIGNS_KEY = 'active';

/**
 * Story 61.4 — campanhas em envio/pausadas/agendadas para as próximas 24 h (faixa do shell).
 * Chave com o tenant (RC-056). Polling de 15 s só enquanto houver lote ativo (60 s fora disso) e
 * invalidação pelo evento `campaign:updated` do namespace /realtime quando o socket já existe.
 */
export function useActiveCampaigns(enabled = true) {
  const tenantSlug = useTenantSlug();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [CAMPAIGNS_KEY, ACTIVE_CAMPAIGNS_KEY, tenantSlug],
    queryFn: listActiveCampaigns,
    enabled: !!tenantSlug && enabled,
    refetchInterval: (q) => (Array.isArray(q.state.data) && q.state.data.length > 0 ? 15000 : 60000),
  });

  useEffect(() => {
    if (!tenantSlug || !enabled) return;
    const socket = getExistingSocket();
    if (!socket) return;
    const listeners = sessionSocketListeners(socket);
    const onUpdated = () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, ACTIVE_CAMPAIGNS_KEY, tenantSlug] });
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
    };
    listeners.on('campaign:updated', onUpdated);
    return () => listeners.off('campaign:updated', onUpdated);
  }, [tenantSlug, enabled, queryClient]);

  return query;
}

export function useCampaigns() {
  return useQuery({
    queryKey: [CAMPAIGNS_KEY],
    queryFn: getCampaigns,
    // RC-015: o envio conclui em worker (fora do request-response) — enquanto houver
    // campanha 'sending', a lista se atualiza sozinha; sem envio ativo, o polling desliga.
    refetchInterval: (query) =>
      Array.isArray(query.state.data) && query.state.data.some((c) => c.status === 'sending') ? 5000 : false,
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: [CAMPAIGNS_KEY, id],
    queryFn: () => getCampaign(id!),
    enabled: !!id,
  });
}

export function useCampaignStats(id: string | undefined, options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: [CAMPAIGNS_KEY, id, 'stats'],
    queryFn: () => getCampaignStats(id!),
    enabled: !!id,
    refetchInterval: options?.refetchInterval,
  });
}

export function useCampaignAudience(params: CampaignAudienceParams = {}) {
  return useQuery({
    queryKey: [CAMPAIGNS_KEY, 'audience', params],
    queryFn: () => getCampaignAudience(params),
  });
}

export function useSegmentationCatalog() {
  return useQuery({
    queryKey: [CAMPAIGN_SEGMENTATION_CATALOG_KEY],
    queryFn: getSegmentationCatalog,
  });
}

export interface SegmentationOption {
  value: string;
  label: string;
}

export interface SegmentationOptionSources {
  services: SegmentationOption[];
  professionals: SegmentationOption[];
  locations: SegmentationOption[];
  kanbanColumns: SegmentationOption[];
  isLoading: boolean;
  isError?: boolean;
  refetch?: () => void;
  sourceStates?: Record<
    'services' | 'professionals' | 'locations' | 'kanbanColumns',
    { isLoading: boolean; isError: boolean; refetch: () => void }
  >;
}

/**
 * Story 37.4 / AC2 — fontes de opção (optionSource) para os filtros select/multiselect do
 * wizard de campanhas: serviços, profissionais, unidades e etapas do funil. Sempre expõe
 * `{ value: id interno, label: nome legível }`, para o wizard enviar o value interno ao backend
 * (em vez de o usuário digitar UUID/enum na mão). Gated por `enabled` para não buscar quando o
 * modal está fechado ou fora do modo "Criar filtro".
 */
export function useSegmentationOptions(enabled: boolean): SegmentationOptionSources {
  const services = useQuery({
    queryKey: ['services', 'campaign-segmentation', { isActive: true }],
    queryFn: getAllSegmentationServices,
    enabled,
  });
  const professionals = useQuery({
    queryKey: ['professionals', 'campaign-segmentation'],
    queryFn: getAllSegmentationProfessionals,
    enabled,
  });
  const locations = useQuery({
    queryKey: ['locations', 'campaign-segmentation', { active: true }],
    queryFn: getAllSegmentationLocations,
    enabled,
  });
  const kanbanColumns = useQuery({
    queryKey: ['kanban-columns'],
    queryFn: getKanbanColumns,
    enabled,
  });

  return {
    services: (services.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    professionals: (professionals.data ?? []).map((p) => ({ value: p.id, label: p.name })),
    locations: (locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
    kanbanColumns: (kanbanColumns.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    isLoading:
      services.isLoading || professionals.isLoading || locations.isLoading || kanbanColumns.isLoading,
    isError: services.isError || professionals.isError || locations.isError || kanbanColumns.isError,
    sourceStates: {
      services: { isLoading: services.isLoading, isError: services.isError, refetch: () => void services.refetch() },
      professionals: { isLoading: professionals.isLoading, isError: professionals.isError, refetch: () => void professionals.refetch() },
      locations: { isLoading: locations.isLoading, isError: locations.isError, refetch: () => void locations.refetch() },
      kanbanColumns: { isLoading: kanbanColumns.isLoading, isError: kanbanColumns.isError, refetch: () => void kanbanColumns.refetch() },
    },
    refetch: () => {
      void services.refetch();
      void professionals.refetch();
      void locations.refetch();
      void kanbanColumns.refetch();
    },
  };
}

export function usePreviewCampaignAudience() {
  // single-flight-exempt: é leitura (prévia, link de arquivo ou exportação): não grava nada
  return useMutation({
    mutationFn: (data: PreviewCampaignAudienceData) => previewCampaignAudience(data),
  });
}

export function usePreviewCampaignMessage() {
  // single-flight-exempt: é leitura (prévia, link de arquivo ou exportação): não grava nada
  return useMutation({
    mutationFn: (data: PreviewCampaignMessageData) => previewCampaignMessage(data),
  });
}

export function useRetryCampaignMessage() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: ({ campaignId, messageId }: { campaignId: string; messageId: string }) => retryCampaignMessage(campaignId, messageId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, variables.campaignId] });
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
      toast.success('Envio preparado para uma nova tentativa. Atualize para acompanhar.');
    },
    onError: (error: AxiosError<{ message?: string }>) => toast.error(error.response?.data?.message || 'Não foi possível preparar uma nova tentativa.'),
  });
}

export function useReconcileCampaignMessage() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: ({
      campaignId,
      messageId,
      blockId,
      outcome,
    }: {
      campaignId: string;
      messageId: string;
      blockId: string;
      outcome: 'accepted' | 'rejected';
    }) => reconcileCampaignMessage(campaignId, messageId, blockId, outcome),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, variables.campaignId] });
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, variables.campaignId, 'stats'] });
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
      toast.success(result.resumed
        ? 'Resultado confirmado. O envio dos próximos blocos foi retomado.'
        : result.outcome === 'rejected'
          ? 'Bloco marcado como não enviado. Você já pode tentar novamente.'
          : 'Resultado confirmado.');
    },
    onError: (error: AxiosError<{ message?: string }>) => toast.error(
      error.response?.data?.message || 'Não foi possível confirmar o resultado deste bloco.',
    ),
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (data: CreateCampaignData) => createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
      toast.success('Campanha criada com sucesso!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao criar campanha';
      toast.error(message);
    },
  });
}

export function useUpdateCampaignDraft() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignDraftData }) => updateCampaignDraft(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
      toast.success('Rascunho atualizado.');
    },
    onError: (error: AxiosError<{ message?: string }>) => toast.error(
      error.response?.data?.message || 'Não foi possível atualizar o rascunho.',
    ),
  });
}

export function useStartCampaign() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (id: string) => startCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, id] });
      toast.success('Campanha iniciada!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao iniciar campanha';
      toast.error(message);
    },
  });
}

export function useCancelCampaign() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (id: string) => cancelCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, id] });
      toast.success('Campanha cancelada!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao cancelar campanha';
      toast.error(message);
    },
  });
}

export function usePauseCampaign() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (id: string) => pauseCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, id] });
      toast.success('Campanha pausada.');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao pausar campanha';
      toast.error(message);
    },
  });
}

export function useResumeCampaign() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (id: string) => resumeCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, id] });
      toast.success('Campanha retomada.');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao retomar campanha';
      toast.error(message);
    },
  });
}

export function usePreviewMessage() {
  // single-flight-exempt: é leitura (prévia, link de arquivo ou exportação): não grava nada
  return useMutation({
    mutationFn: ({ content, patientId }: { content: string; patientId: string }) =>
      previewMessage(content, patientId),
  });
}
